import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { waitMillis } from '../../utils/promise-utils';
import { isHydrated } from '../../utils/utils';
import { Afterpay } from './Afterpay';
import { AfterpayButton } from './afterpay-button/AfterpayButton';
import { AfterPayMessaging } from './afterpay-messaging';
import type { PaymentMethodConfig } from '../../bff-api/baskets/model';
import type { Product } from '../../bff-api/products/model';

interface AfterPaySectionProps {
    eligible?: boolean;
    afterpayConfig?: PaymentMethodConfig;
    mode?: 'default' | 'button' | 'messaging';
    product?: Product;
}

const AfterpaySection: FC<AfterPaySectionProps> = ({
    eligible = false,
    afterpayConfig,
    mode,
    product,
}) => {
    const [inDelay, setInDelay] = useState(false);
    const sdkLink = useCustomPreferenceOrDefault(CustomPreference.AFTERPAY_SDK_LINK, '');

    useEffect(() => {
        async function loadAfterpayScript(): Promise<void> {
            setInDelay(true);
            const script = document.querySelector(`script[src="${sdkLink}"]`);
            if (script) {
                script.remove();
            }

            await waitMillis(500);

            const newScript = document.createElement('script');
            newScript.src = sdkLink;
            newScript.async = true;
            document.body.appendChild(newScript);
            setInDelay(false);
        }

        if (sdkLink) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            loadAfterpayScript();
        }
    }, [eligible, sdkLink]);

    if (!isHydrated() || !afterpayConfig || inDelay) {
        return null;
    }

    if (mode === 'button') {
        return <AfterpayButton afterpayConfig={afterpayConfig} />;
    }

    if (mode === 'messaging') {
        return (
            <AfterPayMessaging
                eligible={eligible}
                product={product}
                afterpayConfig={afterpayConfig}
            />
        );
    }

    return <Afterpay afterpayConfig={afterpayConfig} />;
};

AfterpaySection.defaultProps = {
    mode: 'default',
    product: undefined,
    afterpayConfig: undefined,
    eligible: false,
};

export { AfterpaySection };
